import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  useServerContext,
  useOffers,
  useUserData,
  useNoomSummit,
} from "src/hooks/redux";
import {
  Offer,
  ServerContextState,
} from "src/utils/redux/slices/serverContext";
import {
  updateShippingInfoError,
  updateShippingInfo,
  updateShippingStatus,
  updatePurchasedPlans,
} from "src/utils/redux/slices/offers";
import { NOOM_SUMMIT_PRODUCT_ID } from "src/utils/constants";
import { isNoomSummitDisplayableInStore } from "src/utils/userSegment/features";

export const getSingleInAppOffers = (
  serverContext: ServerContextState,
  noomPremiumPurchased
) => {
  const { offers } = serverContext;
  if (noomPremiumPurchased) {
    return offers["hw-addons-baseline"].single || [];
  }
  return offers["hw-in_app_offers-baseline"]?.single || [];
};

const getShuffledInAppStoreOffers = async (
  serverContext: ServerContextState,
  noomPremiumPurchased: string,
  purchasedNoomSummit: boolean
) => {
  const { offers } = serverContext;

  if (!offers) return [];

  let allOffers = [];

  if (noomPremiumPurchased) {
    allOffers = offers["addons-without-meal-workout-bundle"]?.store || [];
  } else {
    allOffers = offers["hw-in_app_offers-baseline"]?.store || [];
  }

  // Hide Noom Summit from users who have already purchased it or it is not eligible for
  await isNoomSummitDisplayableInStore.load?.();
  if (!isNoomSummitDisplayableInStore() || purchasedNoomSummit) {
    return allOffers.filter(
      (offer) => offer.addon_product_id !== NOOM_SUMMIT_PRODUCT_ID
    );
  }
  return allOffers;
};

export const useInAppOffersContext = () => {
  const serverContext = useServerContext();
  const userData = useUserData();

  const noomPremiumPurchased = userData.NoomPremiumSurveyCompleteDateISO;
  const { purchasedNoomSummit } = useNoomSummit();

  const singleInAppOffers = getSingleInAppOffers(
    serverContext,
    noomPremiumPurchased
  );

  const [inAppOffers, setInAppOffers] = useState<Offer[]>([]);
  useEffect(() => {
    getShuffledInAppStoreOffers(
      serverContext,
      noomPremiumPurchased,
      purchasedNoomSummit
    ).then(setInAppOffers);
  }, [serverContext, noomPremiumPurchased, purchasedNoomSummit]);

  const offers = useOffers();
  const dispatch = useDispatch();

  const setShippingInfo = useCallback(
    (shippingInfo) => {
      dispatch(updateShippingInfo(shippingInfo));
    },
    [dispatch]
  );

  const setShippingInfoError = (shippingInfoError) => {
    dispatch(updateShippingInfoError(shippingInfoError));
  };

  const setShippingStatus = (shippingStatus) => {
    dispatch(updateShippingStatus(shippingStatus));
  };

  const setPurchasedPlans = (purchasedPlans) => {
    dispatch(updatePurchasedPlans(purchasedPlans));
  };

  const { shippingInfo, shippingStatus, shippingInfoError, purchasedPlans } =
    offers;

  const name = userData?.name;
  useEffect(() => {
    if (name) {
      const nameSplit = name.split(" ");
      const firstName = nameSplit[0];
      const lastName = nameSplit.slice(1).join(" ");

      setShippingInfo({
        ...shippingInfo,
        firstName,
        lastName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, setShippingInfo]);

  return {
    inAppOffers,
    singleInAppOffers,
    shippingInfo,
    setShippingInfo,
    shippingInfoError,
    setShippingInfoError,
    shippingStatus,
    setShippingStatus,
    setPurchasedPlans,
    purchasedPlans,
  };
};
